import { useEffect, useState, useCallback } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { ShieldCheck, LogOut, BadgeCheck, Star, Archive, RefreshCw, Pencil, Copy } from "lucide-react";
import axios from "axios";
import { API, setPageMeta } from "@/lib/api";

const adminApi = axios.create({ baseURL: API, withCredentials: true });

const input = "w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";

const Login = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/auth/login", form);
      onLogin(data.user);
      toast.success("Welcome back.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-white/15 p-10">
        <ShieldCheck size={26} className="text-white/60" />
        <h1 className="font-display text-3xl mt-5">CLUB HQ.</h1>
        <p className="font-mono-label text-white/40 mt-2 mb-8">ADMIN ACCESS ONLY</p>
        <div className="space-y-5">
          <input data-testid="admin-email-input" required type="email" placeholder="EMAIL" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
          <input data-testid="admin-password-input" required type="password" placeholder="PASSWORD" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-invert mt-8 w-full bg-white text-ink font-mono-label px-6 py-4 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
          {loading ? "CHECKING..." : "SIGN IN"}
        </button>
      </form>
    </div>
  );
};

const StatTile = ({ label, value, testid }) => (
  <div className="border border-white/10 p-6" data-testid={testid}>
    <p className="font-display text-4xl">{value}</p>
    <p className="font-mono-label text-white/40 mt-2">{label}</p>
  </div>
);

const Queue = () => {
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/submissions", { params: { status } }).then((r) => setSubs(r.data)).finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try {
      const { data } = await adminApi.post(`/admin/submissions/${id}/decision`, { action });
      toast.success(data.created_club ? `Approved. Club published: ${data.created_club}` : data.claimed_club ? `Claim approved for ${data.claimed_club}` : `Submission ${action}d.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed.");
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {[["pending_review", "PENDING"], ["approved", "APPROVED"], ["rejected", "REJECTED"]].map(([s, label]) => (
          <button key={s} data-testid={`queue-status-${s}`} onClick={() => setStatus(s)}
            className={`font-mono-label px-4 py-2.5 border transition-colors duration-200 ${status === s ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}>
            {label}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="font-mono-label text-white/40 py-10 text-center">LOADING...</p>
      ) : subs.length === 0 ? (
        <div className="border border-white/10 p-12 text-center" data-testid="queue-empty">
          <p className="font-display text-2xl">QUEUE CLEAR.</p>
          <p className="text-white/50 text-sm mt-2">Nothing waiting for review. Go for a run.</p>
        </div>
      ) : (
        <div className="space-y-px" data-testid="queue-list">
          {subs.map((s) => (
            <div key={s.id} className="border border-white/10 p-6" data-testid={`submission-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className="font-mono-label bg-white/10 px-2.5 py-1">{s.form_type.toUpperCase()}</span>
                  <p className="font-display text-xl mt-3">{s.payload?.club || s.payload?.org || s.name}</p>
                  <p className="font-mono-label text-white/40 mt-1">{s.name} · {s.email} · {s.created_at?.slice(0, 16).replace("T", " ")}</p>
                </div>
                {s.status === "pending_review" ? (
                  <div className="flex gap-2">
                    <button data-testid={`approve-${s.id}`} onClick={() => decide(s.id, "approve")} className="btn-invert bg-white text-ink font-mono-label px-5 py-2.5 border border-white hover:bg-transparent hover:text-white">APPROVE</button>
                    <button data-testid={`reject-${s.id}`} onClick={() => decide(s.id, "reject")} className="btn-invert border border-white/40 font-mono-label px-5 py-2.5 hover:bg-white hover:text-ink">REJECT</button>
                  </div>
                ) : (
                  <span className="font-mono-label text-white/40">{s.status.toUpperCase()} · {s.reviewed_by}</span>
                )}
              </div>
              {s.payload && Object.keys(s.payload).length > 0 && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-5 border-t border-white/10 pt-4">
                  {Object.entries(s.payload).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="text-sm">
                      <dt className="font-mono-label text-white/30 inline mr-2">{k.toUpperCase()}:</dt>
                      <dd className="inline text-white/70">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ClubEditForm = ({ club, onSave, onCancel }) => {
  const [f, setF] = useState({
    name: club.name || "",
    city: club.city || "",
    state: club.state || "",
    neighborhood: club.neighborhood || "",
    instagram: club.instagram || "",
    website: club.website || "",
    schedule_note: club.schedule_note || "",
    description: club.description || "",
    tags: (club.tags || []).join(", "),
    meet_days: club.meet_days || [],
    priority: club.priority ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleDay = (d) => setF({ ...f, meet_days: f.meet_days.includes(d) ? f.meet_days.filter((x) => x !== d) : [...f.meet_days, d] });

  const save = async () => {
    setSaving(true);
    const ok = await onSave({
      ...f,
      priority: parseInt(f.priority, 10) || 0,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
    setSaving(false);
    if (ok) onCancel();
  };

  const field = (label, key, props = {}) => (
    <label className="block">
      <span className="font-mono-label text-white/40">{label}</span>
      <input data-testid={`club-edit-${key}`} value={f[key]} onChange={set(key)} className={input} {...props} />
    </label>
  );

  return (
    <div data-testid={`club-edit-form-${club.slug}`} className="border border-white/15 p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      {field("NAME", "name")}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">{field("CITY", "city")}</div>
        {field("STATE", "state", { maxLength: 2, placeholder: "TX" })}
      </div>
      {field("NEIGHBORHOOD", "neighborhood")}
      {field("INSTAGRAM (NO @)", "instagram")}
      {field("WEBSITE", "website")}
      {field("PRIORITY", "priority", { type: "number", min: 0 })}
      <label className="block md:col-span-2">
        <span className="font-mono-label text-white/40">SCHEDULE NOTE</span>
        <input data-testid="club-edit-schedule_note" value={f.schedule_note} onChange={set("schedule_note")} className={input} />
      </label>
      <label className="block md:col-span-2">
        <span className="font-mono-label text-white/40">DESCRIPTION</span>
        <textarea data-testid="club-edit-description" value={f.description} onChange={set("description")} rows={3} className="w-full bg-transparent border border-white/30 p-3 text-sm mt-1 focus:border-white transition-colors duration-200" />
      </label>
      {field("TAGS (COMMA SEPARATED)", "tags")}
      <div>
        <span className="font-mono-label text-white/40">MEET DAYS</span>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {DAYS.map((d) => (
            <button key={d} type="button" data-testid={`club-edit-day-${d.toLowerCase()}`} onClick={() => toggleDay(d)}
              className={`font-mono-label px-2.5 py-1.5 border transition-colors duration-150 ${f.meet_days.includes(d) ? "bg-white text-ink border-white" : "border-white/25 text-white/50 hover:border-white"}`}>
              {d.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="md:col-span-2 flex gap-3 pt-2">
        <button type="button" data-testid="club-edit-save" onClick={save} disabled={saving}
          className="btn-invert bg-white text-ink font-mono-label px-6 py-3 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
          {saving ? "SAVING..." : "SAVE CHANGES"}
        </button>
        <button type="button" data-testid="club-edit-cancel" onClick={onCancel} className="font-mono-label px-6 py-3 border border-white/25 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
          CANCEL
        </button>
      </div>
    </div>
  );
};

const ClubsAdmin = () => {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/clubs", { params: { q: q || undefined, page } }).then((r) => setData(r.data));
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const patch = async (slug, updates) => {
    try {
      await adminApi.patch(`/admin/clubs/${slug}`, updates);
      toast.success("Updated.");
      load();
      return true;
    } catch {
      toast.error("Update failed.");
      return false;
    }
  };

  return (
    <div>
      <input data-testid="admin-clubs-search" placeholder="SEARCH CLUBS" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className={`${input} max-w-sm mb-6`} />
      <p className="font-mono-label text-white/40 mb-4">{data.total} CLUBS</p>
      <div data-testid="admin-clubs-list">
        {data.items.map((c) => (
          <div key={c.slug}>
          <div className="grid grid-cols-12 items-center gap-3 border-b border-white/10 py-4" data-testid={`admin-club-${c.slug}`}>
            <div className="col-span-12 md:col-span-5">
              <p className="font-display text-lg leading-tight flex items-center gap-2">
                {c.name}
                {c.priority > 0 && <span className="font-mono-label bg-white/10 px-1.5 py-0.5">P{c.priority}</span>}
                {c.archived && <span className="font-mono-label bg-white/10 px-1.5 py-0.5">ARCHIVED</span>}
              </p>
              <p className="font-mono-label text-white/40 mt-0.5">{c.is_national ? "NATIONAL" : `${c.city}, ${c.state}`} · {c.claim_status}</p>
            </div>
            <div className="col-span-12 md:col-span-7 flex flex-wrap gap-2 md:justify-end">
              <button data-testid={`edit-club-${c.slug}`} onClick={() => setEditing(editing === c.slug ? null : c.slug)}
                className={`flex items-center gap-1.5 font-mono-label px-3 py-2 border transition-colors duration-200 ${editing === c.slug ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"}`}>
                <Pencil size={12} /> EDIT
              </button>
              <button data-testid={`toggle-verified-${c.slug}`} onClick={() => patch(c.slug, { verified: !c.verified })}
                className={`flex items-center gap-1.5 font-mono-label px-3 py-2 border transition-colors duration-200 ${c.verified ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"}`}>
                <BadgeCheck size={12} /> VERIFIED
              </button>
              <button data-testid={`toggle-featured-${c.slug}`} onClick={() => patch(c.slug, { featured: !c.featured })}
                className={`flex items-center gap-1.5 font-mono-label px-3 py-2 border transition-colors duration-200 ${c.featured ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"}`}>
                <Star size={12} /> FEATURED
              </button>
              <button data-testid={`toggle-archived-${c.slug}`} onClick={() => patch(c.slug, { archived: !c.archived })}
                className={`flex items-center gap-1.5 font-mono-label px-3 py-2 border transition-colors duration-200 ${c.archived ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"}`}>
                <Archive size={12} /> ARCHIVE
              </button>
            </div>
          </div>
          {editing === c.slug && (
            <ClubEditForm club={c} onSave={(updates) => patch(c.slug, updates)} onCancel={() => setEditing(null)} />
          )}
          </div>
        ))}
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="font-mono-label border border-white/20 px-4 py-2.5 disabled:opacity-30">PREV</button>
          <span className="font-mono-label text-white/40">PAGE {page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="font-mono-label border border-white/20 px-4 py-2.5 disabled:opacity-30">NEXT</button>
        </div>
      )}
    </div>
  );
};

const DropDemandRow = ({ d, maxDemand, onBlasted }) => {
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const blast = async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post(`/admin/drops/${d.slug}/blast`);
      toast.success(data.message);
      setTimeout(onBlasted, 1200 + (data.queued || 0) * 700);
    } catch {
      toast.error("Blast failed. Try again.");
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-white/10 py-3" data-testid={`drop-demand-${d.slug}`}>
      <span className="text-sm text-white/80 w-40 shrink-0">{d.city || d.name}</span>
      <div className="h-1.5 flex-1 min-w-32 bg-white/10">
        <div className="h-full bg-white" style={{ width: `${(d.count / maxDemand) * 100}%` }} />
      </div>
      <span className="font-display text-xl w-10 text-right">{d.count}</span>
      <span className="font-mono-label text-white/40 w-28">{d.pending > 0 ? `${d.pending} WAITING` : "ALL NOTIFIED"}</span>
      {d.drop_size && <span className="font-mono-label text-white/40 w-24">SOLD {d.sold || 0}/{d.drop_size}</span>}
      {d.pending > 0 && !confirming && (
        <button data-testid={`drop-blast-${d.slug}`} onClick={() => setConfirming(true)}
          className="font-mono-label border border-white/30 px-4 py-2 hover:bg-white hover:text-ink transition-colors duration-200">
          SEND DROP ALERT
        </button>
      )}
      {confirming && (
        <span className="flex gap-2">
          <button data-testid={`drop-blast-confirm-${d.slug}`} onClick={blast} disabled={sending}
            className="font-mono-label bg-white text-ink border border-white px-4 py-2 disabled:opacity-50">
            {sending ? "SENDING..." : `EMAIL ${d.pending} NOW`}
          </button>
          <button onClick={() => setConfirming(false)} className="font-mono-label border border-white/20 text-white/50 px-3 py-2">CANCEL</button>
        </span>
      )}
    </div>
  );
};

const Audience = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    adminApi.get("/admin/audience").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="font-mono-label text-white/40 py-10 text-center">LOADING...</p>;
  const maxDemand = Math.max(1, ...(data.drop_demand || []).map((d) => d.count));
  return (
    <div className="space-y-10">
      {data.sales && (
        <div className="flex flex-wrap gap-10 border border-white/15 p-6" data-testid="audience-sales">
          <div>
            <p className="font-mono-label text-white/40">ITEMS SOLD</p>
            <p className="font-display text-4xl mt-1" data-testid="sales-orders">{data.sales.orders}</p>
          </div>
          <div>
            <p className="font-mono-label text-white/40">REVENUE (TEST MODE)</p>
            <p className="font-display text-4xl mt-1" data-testid="sales-revenue">${(data.sales.revenue_cents / 100).toFixed(2)}</p>
          </div>
          {data.cart_recovery && (
            <>
              <div>
                <p className="font-mono-label text-white/40">ABANDONED CARTS</p>
                <p className="font-display text-4xl mt-1" data-testid="recovery-abandoned">{data.cart_recovery.abandoned}</p>
              </div>
              <div>
                <p className="font-mono-label text-white/40">RECOVERY EMAILS</p>
                <p className="font-display text-4xl mt-1" data-testid="recovery-emails-sent">{data.cart_recovery.emails_sent}</p>
              </div>
              <div>
                <p className="font-mono-label text-white/40">RECOVERED</p>
                <p className="font-display text-4xl mt-1" data-testid="recovery-recovered">
                  {data.cart_recovery.recovered_orders}
                  <span className="text-white/40 text-xl"> · ${(data.cart_recovery.recovered_revenue_cents / 100).toFixed(0)}</span>
                </p>
              </div>
            </>
          )}
        </div>
      )}
      {(data.drop_demand || []).length > 0 && (
        <div data-testid="audience-drop-demand">
          <p className="font-mono-label text-white/40 mb-4">DROP DEMAND · CITY SERIES ({(data.drop_demand || []).reduce((a, d) => a + d.count, 0)} SIGNUPS)</p>
          {data.drop_demand.map((d) => (
            <DropDemandRow key={d.slug} d={d} maxDemand={maxDemand} onBlasted={load} />
          ))}
          <p className="font-mono-label text-white/30 mt-4">SEND DROP ALERT EMAILS EVERYONE STILL WAITING ON THAT TEE. ALREADY-NOTIFIED PEOPLE ARE NEVER EMAILED TWICE.</p>
        </div>
      )}
      {data.search_intel && data.search_intel.total > 0 && (
        <div data-testid="audience-search-intel">
          <p className="font-mono-label text-white/40 mb-4">SEARCH INTEL ({data.search_intel.total} SEARCHES)</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="border border-white/10 p-5">
              <p className="font-mono-label text-white/30 mb-3">TOP SEARCHES</p>
              {data.search_intel.top.map((t) => (
                <div key={t.q} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-white/80 truncate">{t.q}</span>
                  <span className="font-mono-label text-white/40 shrink-0 ml-4">{t.count}×</span>
                </div>
              ))}
              {data.search_intel.top.length === 0 && <p className="text-white/30 text-sm">No searches yet.</p>}
            </div>
            <div className="border border-white/10 p-5" data-testid="search-zero-results">
              <p className="font-mono-label text-white/30 mb-3">ZERO-RESULT SEARCHES · ADD THIS CONTENT NEXT</p>
              {data.search_intel.zero.map((t) => (
                <div key={t.q} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-white/80 truncate">{t.q}</span>
                  <span className="font-mono-label text-white/40 shrink-0 ml-4">{t.count}×</span>
                </div>
              ))}
              {data.search_intel.zero.length === 0 && <p className="text-white/30 text-sm">Nothing missed yet. Good sign.</p>}
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div data-testid="audience-subscribers">
        <p className="font-mono-label text-white/40 mb-4">LATEST SUBSCRIBERS ({data.subscribers.length})</p>
        {data.subscribers.map((s) => (
          <div key={s.id} className="border-b border-white/10 py-3 text-sm flex flex-wrap gap-x-4">
            <span className="text-white/80">{s.email}</span>
            <span className="font-mono-label text-white/40">{[s.city, s.goal, s.source].filter(Boolean).join(" · ")}</span>
          </div>
        ))}
      </div>
      <div data-testid="audience-rsvps">
        <p className="font-mono-label text-white/40 mb-4">LATEST RSVPS ({data.rsvps.length})</p>
        {data.rsvps.map((r) => (
          <div key={r.id} className="border-b border-white/10 py-3 text-sm flex flex-wrap gap-x-4">
            <span className="text-white/80">{r.name} · {r.email}</span>
            <span className="font-mono-label text-white/40">{r.event_id}{r.distance ? ` · ${r.distance}` : ""}</span>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

const OUTREACH_STATUS_LABELS = {
  not_contacted: "NOT CONTACTED",
  dm_sent: "DM SENT",
  emailed: "EMAILED",
  posted: "POSTED ✓",
};

const OutreachRow = ({ c, onUpdate }) => {
  const [email, setEmail] = useState(c.email || "");
  const [sending, setSending] = useState(false);

  const copyDm = async () => {
    try {
      await navigator.clipboard.writeText(c.dm_text);
      toast.success("DM copied. Go paste it in their Instagram.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      const { data } = await adminApi.post(`/admin/outreach/${c.slug}/email`, { email });
      toast.success(data.message);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const mark = async (status) => {
    try {
      await adminApi.post(`/admin/outreach/${c.slug}/status`, { status });
      onUpdate();
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div className="border border-white/10 p-5" data-testid={`outreach-${c.slug}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="font-display text-2xl opacity-40 w-10">#{c.rank}</span>
        <div className="min-w-44">
          <p className="font-display text-lg leading-tight">{c.name}</p>
          <p className="font-mono-label text-white/40">{c.city ? `${c.city}, ${c.state}` : "NATIONAL"} · {c.score}/100</p>
        </div>
        <span data-testid={`outreach-status-${c.slug}`} className={`font-mono-label px-2.5 py-1 ${c.status === "not_contacted" ? "bg-white/10 text-white/50" : "bg-white text-ink"}`}>
          {OUTREACH_STATUS_LABELS[c.status] || c.status}
        </span>
        <div className="flex flex-wrap gap-2 ml-auto">
          {c.instagram && (
            <a href={`https://instagram.com/${c.instagram}`} target="_blank" rel="noopener noreferrer" data-testid={`outreach-ig-${c.slug}`}
              className="font-mono-label border border-white/25 px-3 py-2 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
              @{c.instagram}
            </a>
          )}
          <a href={c.badge_url} target="_blank" rel="noopener noreferrer" data-testid={`outreach-badge-${c.slug}`}
            className="font-mono-label border border-white/25 px-3 py-2 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
            BADGE PNG
          </a>
          <button onClick={copyDm} data-testid={`outreach-copy-dm-${c.slug}`}
            className="flex items-center gap-1.5 font-mono-label border border-white/25 px-3 py-2 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
            <Copy size={12} /> COPY DM
          </button>
          {c.status !== "dm_sent" && c.status !== "posted" && (
            <button onClick={() => mark("dm_sent")} data-testid={`outreach-mark-dm-${c.slug}`}
              className="font-mono-label border border-white/25 px-3 py-2 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
              MARK DM SENT
            </button>
          )}
          {c.status !== "posted" && (
            <button onClick={() => mark("posted")} data-testid={`outreach-mark-posted-${c.slug}`}
              className="font-mono-label border border-white/25 px-3 py-2 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
              MARK POSTED
            </button>
          )}
        </div>
      </div>
      <form onSubmit={sendEmail} className="flex gap-2 mt-4 max-w-lg">
        <input type="email" placeholder="CLUB CONTACT EMAIL (IF YOU HAVE ONE)" value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid={`outreach-email-input-${c.slug}`} className={`${input} flex-1`} />
        <button type="submit" disabled={sending || !email} data-testid={`outreach-email-send-${c.slug}`}
          className="font-mono-label bg-white text-ink px-4 py-2 border border-white disabled:opacity-40 whitespace-nowrap">
          {sending ? "SENDING…" : "SEND BADGE EMAIL"}
        </button>
      </form>
    </div>
  );
};

const Outreach = () => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/outreach").then((r) => setData(r.data)).catch(() => toast.error("Failed to load outreach list."));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="font-mono-label text-white/40 py-10 text-center">LOADING...</p>;
  const contacted = data.items.filter((c) => c.status !== "not_contacted").length;

  return (
    <div data-testid="admin-outreach">
      <div className="border border-white/15 p-6 mb-8 max-w-3xl">
        <p className="font-display text-2xl">"YOU MADE THE 2026 LIST" OUTREACH.</p>
        <p className="text-white/50 text-sm mt-2">
          Each ranked club gets a hosted badge PNG and a prewritten pitch linking /ranked. Most clubs live on Instagram —
          COPY DM, paste it to their account, then MARK DM SENT. Have a real contact email? SEND BADGE EMAIL delivers the
          branded version. Every badge a club posts is a link back to your rankings.
        </p>
        <p className="font-mono-label text-white/40 mt-4">{contacted} OF {data.items.length} CONTACTED</p>
      </div>
      <div className="space-y-3">
        {data.items.map((c) => (
          <OutreachRow key={c.slug} c={c} onUpdate={load} />
        ))}
      </div>
    </div>
  );
};

const Inbox = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/inbound").then((r) => setData(r.data)).catch(() => toast.error("Failed to load inbox."));
  }, []);

  if (!data) return <p className="font-mono-label text-white/40 py-10 text-center">LOADING...</p>;

  return (
    <div data-testid="admin-inbox">
      <div className="border border-white/15 p-6 mb-8 max-w-3xl">
        <p className="font-display text-2xl">INBOUND EMAIL.</p>
        <p className="text-white/50 text-sm mt-2">
          Everything sent to your ninjarunclub.com addresses lands here (and gets forwarded to your personal inbox with
          reply-to set to the sender). Club replies to outreach and correction emails show up automatically.
        </p>
      </div>
      {data.items.length === 0 ? (
        <p className="font-mono-label text-white/40 border border-white/10 p-10 text-center" data-testid="inbox-empty">
          NO INBOUND EMAIL YET. ONCE THE WEBHOOK IS REGISTERED IN RESEND, REPLIES LAND HERE.
        </p>
      ) : (
        <div className="space-y-3">
          {data.items.map((m) => (
            <details key={m.email_id} className="border border-white/10 p-5" data-testid={`inbox-${m.email_id}`}>
              <summary className="cursor-pointer list-none flex flex-wrap items-center gap-x-5 gap-y-1">
                <span className="font-display text-lg">{m.subject || "(NO SUBJECT)"}</span>
                <span className="font-mono-label text-white/40">{m.from_addr}</span>
                <span className="font-mono-label text-white/30 ml-auto">{(m.received_at || m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {m.status !== "processed" && <span className="font-mono-label bg-white/10 text-white/50 px-2 py-0.5">{(m.status || "").toUpperCase()}</span>}
                {(m.attachments || []).length > 0 && <span className="font-mono-label text-white/40">{m.attachments.length} ATTACHMENT{m.attachments.length > 1 ? "S" : ""}</span>}
              </summary>
              <p className="text-white/60 text-sm mt-4 whitespace-pre-wrap max-h-96 overflow-y-auto">{m.text || "(no plain-text body)"}</p>
              {m.from_addr && (
                <a href={`mailto:${m.from_addr}`} className="font-mono-label link-underline text-white/60 inline-block mt-4">REPLY TO {m.from_addr} →</a>
              )}
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

const sourceLabel = (raw) => {
  const s = raw.replace(/:/g, "-");
  if (s === "shop") return "SHOP PAGE";
  if (s === "exit-shop") return "SHOP · EXIT INTENT";
  if (s.startsWith("exit-product-")) return `PRODUCT (EXIT) · ${s.slice(13).replace(/-/g, " ").toUpperCase()}`;
  if (s.startsWith("product-")) return `PRODUCT · ${s.slice(8).replace(/-/g, " ").toUpperCase()}`;
  if (s.startsWith("exit-collection-")) return `COLLECTION (EXIT) · ${s.slice(16).replace(/-/g, " ").toUpperCase()}`;
  if (s.startsWith("collection-")) return `COLLECTION · ${s.slice(11).replace(/-/g, " ").toUpperCase()}`;
  return s.replace(/-/g, " ").toUpperCase();
};

const LeadSourceChart = ({ sources }) => {
  if (!sources || sources.length === 0) return null;
  const max = sources[0].leads;
  return (
    <div className="mb-6" data-testid="lead-source-chart">
      <p className="font-mono-label text-white/40 mb-3">TOP CAPTURE PAGES</p>
      <div className="space-y-2">
        {sources.slice(0, 10).map((s) => (
          <div key={s.source} data-testid={`lead-source-row-${s.source}`}>
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <span className="font-mono-label text-white/70 truncate">{sourceLabel(s.source)}</span>
              <span className="font-mono-label text-white/50 shrink-0">
                {s.leads} {s.leads === 1 ? "LEAD" : "LEADS"}{s.converted ? ` · ${s.converted} CONVERTED` : ""}
              </span>
            </div>
            <div className="h-2 bg-white/5 mt-1">
              <div className="h-full bg-white/70" style={{ width: `${Math.max(4, Math.round((s.leads / max) * 100))}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OfferLeads = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/offer-leads").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const rate = data.total ? Math.round((data.converted / data.total) * 100) : 0;
  return (
    <div className="border border-white/10 p-6 mt-8" data-testid="sales-offer-leads">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-mono-label text-white/40">25% OFF LEADS</p>
        <p className="font-mono-label text-white/40" data-testid="offer-leads-stats">
          {data.total} CAPTURED · {data.converted} CONVERTED · {rate}%
        </p>
      </div>
      <LeadSourceChart sources={data.sources} />
      {data.items.length === 0 && <p className="text-white/30 text-sm">No leads captured yet.</p>}
      <div className="max-h-80 overflow-y-auto">
        {data.items.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 py-2.5 text-sm" data-testid={`offer-lead-${l.id}`}>
            <span className="text-white/80 truncate">{l.email}</span>
            <span className="font-mono-label text-white/70 flex items-center gap-3 shrink-0">
              {l.code} · {(l.source || "shop").toUpperCase()} · {(l.created_at || "").slice(0, 10)}
              {l.winback_sent_at && !l.converted && (
                <span className="border border-white/20 text-white/50 px-2 py-0.5" data-testid={`offer-lead-winback-${l.id}`}>REISSUED</span>
              )}
              {l.converted ? (
                <span className="border border-white/60 text-white px-2 py-0.5">CONVERTED</span>
              ) : (
                <span className="border border-white/20 text-white/50 px-2 py-0.5">OPEN</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const WaitlistPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/waitlist").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const max = data.cities[0]?.total || 1;
  return (
    <div className="border border-white/10 p-6 mt-8" data-testid="waitlist-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <p className="font-mono-label text-white/40">WAITLIST DEMAND · BY CITY</p>
        <p className="font-mono-label text-white/40" data-testid="waitlist-total">{data.total} SIGN-UPS</p>
      </div>
      <p className="text-white/40 text-xs mb-5">Premium line and Drop 02 interest — produce the loudest cities first.</p>
      {data.cities.length === 0 && <p className="text-white/30 text-sm">No waitlist sign-ups yet.</p>}
      <div className="space-y-4">
        {data.cities.slice(0, 15).map((c) => (
          <div key={c.city} data-testid={`waitlist-city-row-${c.city.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <span className="font-mono-label text-white/80">{c.city.toUpperCase()}</span>
              <span className="font-mono-label text-white/50 shrink-0">{c.total} {c.total === 1 ? "SIGN-UP" : "SIGN-UPS"}</span>
            </div>
            <div className="h-2 bg-white/5 mt-1">
              <div className="h-full bg-white/70" style={{ width: `${Math.max(4, Math.round((c.total / max) * 100))}%` }} />
            </div>
            <p className="text-white/35 text-[11px] mt-1 font-mono-label">
              {c.products.slice(0, 4).map((p) => `${p.line} ${p.count}`).join(" · ")}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlastPanel = () => {
  const [blast, setBlast] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.get("/admin/blast/color-drop").then((r) => setBlast(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!blast) return null;
  const rec = blast.blast;

  const sendTest = async () => {
    if (!testEmail.includes("@")) return toast.error("Enter a valid email.");
    setBusy(true);
    try {
      await adminApi.post("/admin/blast/color-drop/test", { email: testEmail });
      toast.success(`Test sent to ${testEmail}.`);
    } catch {
      toast.error("Test send failed - check the Resend key.");
    }
    setBusy(false);
  };

  const sendBlast = async (force = false) => {
    if (!force && !window.confirm(`Send the Color Drop blast to ${blast.total_unique} unique recipients?`)) return;
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/blast/color-drop/send", { force });
      toast.success(`Blast queued to ${r.data.queued} runners.`);
      setTimeout(load, 2000);
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail} Resend to everyone?`)) { setBusy(false); return sendBlast(true); }
        toast.info("Blast already sent — not resent.");
      } else {
        toast.error(e.response?.data?.detail || "Send failed.");
      }
    }
    setBusy(false);
  };

  return (
    <div className="border border-white/10 p-6 mt-8" data-testid="blast-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="font-mono-label text-white/40">COLOR DROP BLAST · ONE-TIME CAMPAIGN</p>
        {rec && (
          <p className="font-mono-label text-white/60" data-testid="blast-status">
            {rec.status === "sending" ? `IN PROGRESS · ${rec.sent}/${rec.total}` : `${rec.status.toUpperCase()} · ${rec.sent}/${rec.total} SENT`}{rec.failed ? ` · ${rec.failed} FAILED` : ""}
          </p>
        )}
      </div>
      <p className="text-white/50 text-sm mt-2 max-w-2xl">
        "{blast.subject}" — announces the 20 full-color city drops, the $75 bundle, and teases the five next-up designs.
      </p>
      <div className="flex flex-wrap gap-8 mt-4">
        <div><p className="font-mono-label text-white/40">SUBSCRIBERS</p><p className="font-display text-3xl mt-1" data-testid="blast-subscribers">{blast.subscribers}</p></div>
        <div><p className="font-mono-label text-white/40">WAITLIST</p><p className="font-display text-3xl mt-1" data-testid="blast-waitlist">{blast.waitlist}</p></div>
        <div><p className="font-mono-label text-white/40">UNIQUE SEND LIST</p><p className="font-display text-3xl mt-1" data-testid="blast-total">{blast.total_unique}</p></div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button data-testid="blast-preview-btn" onClick={() => setShowPreview(!showPreview)}
          className="font-mono-label border border-white/25 px-5 py-3 text-white/70 hover:border-white transition-colors duration-200">
          {showPreview ? "HIDE PREVIEW" : "PREVIEW EMAIL"}
        </button>
        <input data-testid="blast-test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
          placeholder="you@email.com" type="email"
          className="bg-transparent border border-white/25 font-mono-label text-white px-4 py-3 w-56 focus:border-white outline-none" />
        <button data-testid="blast-test-btn" onClick={sendTest} disabled={busy}
          className="font-mono-label border border-white/25 px-5 py-3 text-white/70 hover:border-white transition-colors duration-200 disabled:opacity-50">
          SEND TEST
        </button>
        <button data-testid="blast-send-btn" onClick={() => sendBlast(false)} disabled={busy || blast.total_unique === 0}
          className="font-mono-label bg-white text-ink border border-white px-6 py-3 hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50">
          {rec ? "RESEND BLAST" : "SEND BLAST"}
        </button>
      </div>
      {showPreview && (
        <iframe title="Blast preview" srcDoc={blast.html} data-testid="blast-preview-frame"
          className="w-full mt-5 border border-white/15 bg-black" style={{ height: 640 }} />
      )}
    </div>
  );
};

const SalesPanel = () => {
  const [sales, setSales] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/sales").then((r) => setSales(r.data)).catch(() => toast.error("Failed to load sales."));
  }, []);
  if (!sales) return <p className="font-mono-label text-white/40">LOADING SALES...</p>;
  const { totals, daily, best_sellers, cities } = sales;
  return (
    <div data-testid="admin-sales-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        <StatTile label="REVENUE" value={`$${totals.revenue.toLocaleString()}`} testid="stat-revenue" />
        <StatTile label="ORDERS" value={totals.orders} testid="stat-orders" />
        <StatTile label="AVG ORDER" value={`$${totals.aov}`} testid="stat-aov" />
        <StatTile label="TO SHIP" value={totals.to_ship} testid="stat-to-ship" />
      </div>
      <div className="border border-white/10 p-6 mt-8" data-testid="sales-chart">
        <p className="font-mono-label text-white/40 mb-4">REVENUE · LAST 30 DAYS</p>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={daily} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={{ stroke: "#2a2a2a" }} interval={4} />
              <YAxis tick={{ fill: "#666", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.06)" }}
                contentStyle={{ background: "#0A0A0A", border: "1px solid #2a2a2a", fontSize: 12 }}
                labelStyle={{ color: "#999" }}
                formatter={(v) => [`$${Number(v).toFixed(2)}`, "REVENUE"]}
              />
              <Bar dataKey="revenue" fill="#ffffff" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <div className="border border-white/10 p-6" data-testid="sales-best-sellers">
          <p className="font-mono-label text-white/40 mb-4">BEST SELLERS</p>
          {best_sellers.length === 0 && <p className="text-white/30 text-sm">No sales yet.</p>}
          {best_sellers.map((b) => (
            <div key={b.name} className="flex items-center justify-between border-b border-white/5 py-2.5 text-sm">
              <span className="text-white/80 truncate">{b.name}</span>
              <span className="font-mono-label text-white/40 shrink-0 ml-4">{b.units} SOLD · ${b.revenue.toFixed(0)}</span>
            </div>
          ))}
        </div>
        <div className="border border-white/10 p-6" data-testid="sales-cities">
          <p className="font-mono-label text-white/40 mb-4">REVENUE BY CITY LINE</p>
          {cities.length === 0 && <p className="text-white/30 text-sm">No sales yet.</p>}
          {cities.map((c) => (
            <div key={c.city} className="flex items-center justify-between border-b border-white/5 py-2.5 text-sm">
              <span className="text-white/80">{c.city.toUpperCase()}</span>
              <span className="font-mono-label text-white/40 shrink-0 ml-4">{c.units} SOLD · ${c.revenue.toFixed(0)}</span>
            </div>
          ))}
        </div>
      </div>
      <OfferLeads />
    </div>
  );
};

const Orders = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [forms, setForms] = useState({});

  const load = useCallback(() => {
    adminApi.get("/admin/orders").then((r) => setData(r.data)).catch(() => toast.error("Failed to load orders."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (id, k, v) => setForms((f) => ({ ...f, [id]: { ...f[id], [k]: v } }));

  const ship = async (o) => {
    const f = forms[o.session_id] || {};
    if (!o.customer_email && !f.email) {
      toast.error("No customer email on this order — enter one first.");
      return;
    }
    setBusy(o.session_id);
    try {
      const { data: res } = await adminApi.post(`/admin/orders/${o.session_id}/ship`, {
        email: f.email || undefined,
        tracking_url: f.tracking_url || undefined,
        carrier: f.carrier || undefined,
      });
      toast.success(res.email_sent ? `Shipped email sent to ${res.recipient}.` : "Marked shipped, but the email failed to send.");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send shipped email.");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="font-mono-label text-white/40">LOADING ORDERS...</p>;

  return (
    <div data-testid="admin-orders">
      <p className="font-display text-2xl mb-2">PAID ORDERS ({data.orders.length}).</p>
      <p className="text-white/40 text-sm mb-8">Send the branded "it's on the move" email when an order ships. Tracking link and carrier are optional. Orders placed before order numbers launched have no stored email — enter one manually.</p>
      {data.orders.length === 0 && <p className="text-white/30 text-sm">No paid orders yet.</p>}
      <div className="space-y-3">
        {data.orders.map((o) => (
          <details key={o.session_id} className="border border-white/10 p-5" data-testid={`order-${o.order_ref || o.session_id}`}>
            <summary className="cursor-pointer list-none flex flex-wrap items-center gap-x-5 gap-y-1">
              <span className="font-display text-lg">{o.order_ref || "(NO REF)"}</span>
              <span className="text-white/70 text-sm">{o.product_name}{o.size ? ` · ${o.size}` : ""}{o.quantity > 1 ? ` × ${o.quantity}` : ""}</span>
              <span className="font-mono-label text-white/40">${((o.amount || 0) / 100).toFixed(2)}</span>
              <span className="font-mono-label text-white/30">{o.customer_email || "NO EMAIL"}</span>
              <span className="font-mono-label text-white/30 ml-auto">{(o.created_at || "").slice(0, 10)}</span>
              {o.shipped_at ? (
                <span className="font-mono-label bg-white text-ink px-2 py-0.5">SHIPPED {(o.shipped_at || "").slice(0, 10)}</span>
              ) : (
                <span className="font-mono-label bg-white/10 text-white/50 px-2 py-0.5">PAID</span>
              )}
              {o.fit_followup_sent_at && (
                <span className="font-mono-label border border-white/25 text-white/50 px-2 py-0.5">FOLLOW-UP SENT</span>
              )}
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
              {!o.customer_email && (
                <input
                  data-testid={`ship-email-${o.order_ref || o.session_id}`}
                  type="email"
                  placeholder="CUSTOMER EMAIL"
                  value={forms[o.session_id]?.email || ""}
                  onChange={(e) => setField(o.session_id, "email", e.target.value)}
                  className={input}
                />
              )}
              <input
                data-testid={`ship-carrier-${o.order_ref || o.session_id}`}
                type="text"
                placeholder="CARRIER (OPTIONAL, E.G. USPS)"
                value={forms[o.session_id]?.carrier || ""}
                onChange={(e) => setField(o.session_id, "carrier", e.target.value)}
                className={input}
              />
              <input
                data-testid={`ship-tracking-${o.order_ref || o.session_id}`}
                type="url"
                placeholder="TRACKING URL (OPTIONAL)"
                value={forms[o.session_id]?.tracking_url || ""}
                onChange={(e) => setField(o.session_id, "tracking_url", e.target.value)}
                className={input}
              />
            </div>
            <button
              data-testid={`ship-send-${o.order_ref || o.session_id}`}
              onClick={() => ship(o)}
              disabled={busy === o.session_id}
              className="btn-invert bg-white text-ink font-mono-label px-6 py-3 border border-white hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50 mt-4"
            >
              {busy === o.session_id ? "SENDING..." : o.shipped_at ? "RESEND SHIPPED EMAIL" : "SEND SHIPPED EMAIL"}
            </button>
            {o.tracking_url && (
              <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="font-mono-label link-underline text-white/60 inline-block mt-4 ml-6">TRACKING →</a>
            )}
          </details>
        ))}
      </div>
    </div>
  );
};

const REDO_FILTERS = [["", "ALL"], ["pending", "PENDING"], ["syncing", "SYNCING"], ["retrying", "RETRYING"], ["synced", "SYNCED"], ["failed", "FAILED"], ["requires_review", "REQUIRES REVIEW"]];

const redoBadge = (s) =>
  s === "synced" ? "bg-white text-ink" :
  s === "failed" || s === "requires_review" ? "border border-white text-white" :
  "bg-white/10 text-white/60";

const RedoSync = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/redo/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    adminApi.get("/admin/redo/orders", { params: { status: status || undefined, q: search || undefined, page } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load Redo sync orders."));
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);

  const retry = async (o) => {
    if (confirming !== o.id) {
      setConfirming(o.id);
      return;
    }
    setConfirming(null);
    setBusy(o.id);
    try {
      await adminApi.post(`/admin/redo/orders/${o.id}/retry`);
      toast.success(`${o.order_number} queued for Redo retry.`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Retry failed.");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="font-mono-label text-white/40">LOADING REDO SYNC...</p>;

  return (
    <div data-testid="redo-sync-panel">
      <p className="font-display text-2xl mb-2">REDO SYNC ({data.total}).</p>
      <p className="text-white/40 text-sm mb-6">Paid Stripe orders and their Redo synchronization state. Retry is available only for FAILED or REQUIRES REVIEW orders and needs a second click to confirm.</p>
      {config && (
        <div data-testid="redo-config-panel" className="border border-white/15 p-5 mb-6">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="font-mono-label text-white/40">REDO CONNECTION</span>
            <span data-testid="redo-config-mode" className={`font-mono-label px-2 py-0.5 ${config.live_ready ? "bg-white text-ink" : "border border-white/40 text-white/70"}`}>
              {config.mode === "mock" ? "MOCK (PREVIEW)" : config.mode === "off" ? "SYNC OFF" : config.live_ready ? "LIVE" : "LIVE (INCOMPLETE)"}
            </span>
            <span data-testid="redo-config-api-secret" className="font-mono-label text-white/50">API SECRET {config.api_secret_configured ? "✓" : "✗"}</span>
            <span data-testid="redo-config-webhook-secret" className="font-mono-label text-white/50">WEBHOOK SECRET {config.webhook_secret_configured ? "✓" : "✗"}</span>
            <span className="font-mono-label text-white/30">STORE {config.store_id}</span>
          </div>
          {!config.live_ready && (
            <div className="text-white/50 text-sm mt-4 space-y-1.5" data-testid="redo-golive-checklist">
              <p className="font-mono-label text-white/40">GO-LIVE CHECKLIST</p>
              <p>1. In the production environment set REDO_API_SECRET (Redo API client, orders_write scope), your real REDO_WEBHOOK_SECRET, and REDO_SYNC_MODE=live.</p>
              <p>2. In Redo, point the Returns webhook at <span className="text-white/80 break-all">{config.webhook_url}</span> with Authorization: Bearer &lt;REDO_WEBHOOK_SECRET&gt;.</p>
              <p>3. Deploy, place one small order, and confirm it shows SYNCED here. Rollback: set REDO_SYNC_MODE=off — checkout keeps working, orders queue for retry.</p>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        {REDO_FILTERS.map(([s, label]) => (
          <button key={s || "all"} data-testid={`redo-filter-${s || "all"}`}
            onClick={() => { setStatus(s); setPage(1); setConfirming(null); }}
            className={`font-mono-label px-3.5 py-2 border transition-colors duration-200 ${status === s ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}>
            {label}{s && data.counts?.[s] ? ` (${data.counts[s]})` : ""}
          </button>
        ))}
      </div>
      <form className="flex gap-3 mb-6" onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); setPage(1); }}>
        <input data-testid="redo-search-input" type="text" placeholder="SEARCH ORDER #, EMAIL, STRIPE ID, REDO ID"
          value={q} onChange={(e) => setQ(e.target.value)} className={input} />
        <button type="submit" data-testid="redo-search-btn" className="btn-invert font-mono-label border border-white/40 px-5 hover:bg-white hover:text-ink shrink-0">SEARCH</button>
      </form>
      {data.orders.length === 0 && <p className="text-white/30 text-sm" data-testid="redo-empty">No orders match.</p>}
      <div className="space-y-3">
        {data.orders.map((o) => (
          <details key={o.id} className="border border-white/10 p-5" data-testid={`redo-order-${o.order_number}`}>
            <summary className="cursor-pointer list-none flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <span className="font-display text-lg">{o.order_number}</span>
              <span className="text-white/70 text-sm">{(o.line_items || []).map((li) => `${li.title}${li.size ? ` · ${li.size}` : ""}`).join(" + ")}</span>
              <span className="font-mono-label text-white/40">${((o.total || 0) / 100).toFixed(2)}</span>
              <span className="font-mono-label text-white/30">{o.customer_email || "NO EMAIL"}</span>
              <span className="font-mono-label text-white/30 ml-auto">{(o.created_at || "").slice(0, 10)}</span>
              <span data-testid={`redo-status-${o.order_number}`} className={`font-mono-label px-2 py-0.5 ${redoBadge(o.redo_status)}`}>
                {(o.redo_status || "").replace("_", " ").toUpperCase()}
              </span>
              {o.return_status && <span className="font-mono-label border border-white/25 text-white/60 px-2 py-0.5">RETURN {o.return_status.toUpperCase()}</span>}
              {o.refund_status && <span className="font-mono-label border border-white/25 text-white/60 px-2 py-0.5">{o.refund_status.replace("_", " ").toUpperCase()}</span>}
            </summary>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 mt-5 border-t border-white/10 pt-4 text-sm">
              {[["CUSTOMER", `${o.customer_name || "—"} · ${o.customer_email || "—"}`],
                ["STRIPE PAYMENT", o.payment_status],
                ["STRIPE SESSION", o.session_id],
                ["PAYMENT INTENT", o.payment_intent_id || "—"],
                ["REDO ORDER ID", o.redo_order_id || "—"],
                ["ATTEMPTS", String(o.attempts || 0)],
                ["LAST ATTEMPT", o.last_attempt_at ? o.last_attempt_at.slice(0, 19).replace("T", " ") : "—"],
                ["NEXT RETRY", o.next_attempt_at ? o.next_attempt_at.slice(0, 19).replace("T", " ") : "—"],
                ["FULFILLMENT", o.fulfillment_status || "—"],
                ["RETURNS", o.returns_count ? `${o.returns_count} (${o.return_status})` : "—"],
                ["REFUND", o.refund_status || "—"],
                ["SYNC MODE", o.mode === "mock" ? "MOCK (preview)" : "LIVE"]].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono-label text-white/30 inline mr-2">{k}:</dt>
                  <dd className="inline text-white/70 break-all">{v}</dd>
                </div>
              ))}
            </dl>
            {o.error && (
              <p data-testid={`redo-error-${o.order_number}`} className="text-white/60 text-sm mt-3 border border-white/20 p-3">
                <span className="font-mono-label text-white/40 mr-2">LAST ERROR:</span>{o.error}
              </p>
            )}
            {(o.redo_status === "failed" || o.redo_status === "requires_review") && (
              <button
                data-testid={`redo-retry-${o.order_number}`}
                onClick={() => retry(o)}
                disabled={busy === o.id}
                className="btn-invert bg-white text-ink font-mono-label px-6 py-3 border border-white hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50 mt-4"
              >
                {busy === o.id ? "RETRYING..." : confirming === o.id ? "CONFIRM RETRY?" : "RETRY REDO SYNC"}
              </button>
            )}
          </details>
        ))}
      </div>
      {data.pages > 1 && (
        <div className="flex items-center gap-4 mt-6">
          <button data-testid="redo-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="font-mono-label border border-white/20 px-4 py-2 disabled:opacity-30 hover:border-white">PREV</button>
          <span className="font-mono-label text-white/40">PAGE {data.page} / {data.pages}</span>
          <button data-testid="redo-next" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
            className="font-mono-label border border-white/20 px-4 py-2 disabled:opacity-30 hover:border-white">NEXT</button>
        </div>
      )}
    </div>
  );
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

const StockPanel = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [drops, setDrops] = useState({});
  const [dateEdits, setDateEdits] = useState({});
  const [digestBusy, setDigestBusy] = useState(false);
  useEffect(() => {
    adminApi.get("/admin/stock").then((r) => setData(r.data)).catch(() => toast.error("Could not load stock."));
    adminApi.get("/drops").then((r) => setDrops(r.data || {})).catch(() => {});
  }, []);
  if (!data) return <p className="font-mono-label text-white/40 mt-8">LOADING STOCK…</p>;
  const needle = q.toLowerCase();
  const items = data.products.filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.slug.includes(needle) || (p.kind || "").includes(needle));
  const toggle = async (p) => {
    const next = p.status === "available" ? "waitlist" : "available";
    setBusy(p.slug);
    try {
      const { data: res } = await adminApi.put(`/admin/stock/${p.slug}`, { status: next });
      setData((d) => ({ ...d, products: d.products.map((x) => (x.slug === p.slug ? { ...x, status: res.status, overridden: res.overridden } : x)) }));
      toast.success(`${p.name} → ${next === "available" ? "IN STOCK" : "WAITLIST"}`);
    } catch {
      toast.error("Toggle failed.");
    } finally {
      setBusy(null);
    }
  };
  const liveCount = data.products.filter((p) => p.status === "available").length;
  const saveDrop = async (p) => {
    const val = dateEdits[p.slug] ?? toLocalInput(drops[p.slug]);
    if (!val) return toast.error("Pick a date first.");
    try {
      const { data: res } = await adminApi.put(`/admin/drops/${p.slug}`, { drop_at: new Date(val).toISOString() });
      setDrops((d) => ({ ...d, [p.slug]: res.drop_at }));
      toast.success(`Drop date set for ${p.name}.`);
    } catch {
      toast.error("Could not set drop date.");
    }
  };
  const clearDrop = async (p) => {
    try {
      await adminApi.put(`/admin/drops/${p.slug}`, { drop_at: null });
      setDrops((d) => { const n = { ...d }; delete n[p.slug]; return n; });
      setDateEdits((d) => { const n = { ...d }; delete n[p.slug]; return n; });
      toast.success("Drop date cleared.");
    } catch {
      toast.error("Could not clear drop date.");
    }
  };
  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data: res } = await adminApi.post("/admin/waitlist-digest/send");
      if (res.ok) toast.success(`Digest sent — ${res.new_this_week} new sign-ups this week, ${res.total} all-time.`);
      else toast.info("No waitlist sign-ups yet — nothing to send.");
    } catch {
      toast.error("Could not send digest.");
    } finally {
      setDigestBusy(false);
    }
  };
  return (
    <div className="mt-8" data-testid="stock-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <p className="font-mono-label text-white/40">STOCK CONTROL · {data.products.length} PRODUCTS</p>
          <p className="text-white/40 text-xs mt-1">Flip a product to WAITLIST to pause checkout instantly — no redeploy. Set a drop date on waitlisted items to show shoppers a live countdown.</p>
        </div>
        <div className="flex items-center gap-4">
          <p className="font-mono-label text-white/40" data-testid="stock-live-count">{liveCount} IN STOCK · {data.products.length - liveCount} WAITLIST</p>
          <button
            onClick={sendDigest}
            disabled={digestBusy}
            data-testid="waitlist-digest-send"
            className="font-mono-label border border-white/25 px-3 py-2 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200 disabled:opacity-40"
          >
            {digestBusy ? "SENDING…" : "SEND WAITLIST DIGEST"}
          </button>
        </div>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search products, cities, kinds…"
        data-testid="stock-search-input"
        className="w-full max-w-md bg-transparent border border-white/20 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white outline-none mb-5"
      />
      <div className="border border-white/10 divide-y divide-white/10 max-h-[60vh] overflow-y-auto">
        {items.map((p) => (
          <div key={p.slug} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`stock-row-${p.slug}`}>
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{p.name}</p>
              <p className="font-mono-label text-white/30 mt-0.5">{(p.kind || "core").toUpperCase()} · ${p.price}{p.overridden ? " · OVERRIDE" : ""}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {p.status !== "available" && (
                <div className="hidden sm:flex items-center gap-1.5">
                  <input
                    type="datetime-local"
                    value={dateEdits[p.slug] ?? toLocalInput(drops[p.slug])}
                    onChange={(e) => setDateEdits((d) => ({ ...d, [p.slug]: e.target.value }))}
                    data-testid={`drop-date-input-${p.slug}`}
                    className="bg-transparent border border-white/20 px-2 py-1 text-xs text-white/70 [color-scheme:dark] focus:border-white outline-none"
                  />
                  <button
                    onClick={() => saveDrop(p)}
                    data-testid={`drop-date-save-${p.slug}`}
                    className="font-mono-label border border-white/25 px-2 py-1.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200"
                  >
                    SET
                  </button>
                  {drops[p.slug] && (
                    <button
                      onClick={() => clearDrop(p)}
                      data-testid={`drop-date-clear-${p.slug}`}
                      title="Clear drop date"
                      className="font-mono-label border border-white/25 px-2 py-1.5 text-white/50 hover:border-white hover:text-white transition-colors duration-200"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
              <span className={`font-mono-label px-2 py-1 border ${p.status === "available" ? "border-white/60 text-white" : "border-white/20 text-white/40"}`} data-testid={`stock-status-${p.slug}`}>
                {p.status === "available" ? "IN STOCK" : "WAITLIST"}
              </span>
              <button
                onClick={() => toggle(p)}
                disabled={busy === p.slug}
                data-testid={`stock-toggle-${p.slug}`}
                className="font-mono-label border border-white/25 px-3 py-1.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200 disabled:opacity-40"
              >
                {busy === p.slug ? "…" : p.status === "available" ? "PAUSE" : "GO LIVE"}
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/30 text-sm px-4 py-6">No products match.</p>}
      </div>
    </div>
  );
};

const TABS = [["overview", "OVERVIEW"], ["queue", "MODERATION QUEUE"], ["clubs", "CLUBS"], ["orders", "ORDERS"], ["redo", "REDO SYNC"], ["stock", "STOCK"], ["audience", "AUDIENCE"], ["outreach", "OUTREACH"], ["inbox", "INBOX"]];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("queue");
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    setPageMeta("Club HQ | Ninja Run Club Admin", "Admin moderation dashboard.");
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    localStorage.removeItem("nrc_admin_token");
    adminApi.get("/auth/admin/me")
      .then((r) => setUser(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
    return () => m.remove();
  }, []);

  useEffect(() => {
    if (user) adminApi.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [user, tab]);

  if (checking) return <p className="font-mono-label text-white/40 py-32 text-center">CHECKING ACCESS...</p>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="bg-night min-h-screen px-5 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-8">
        <div>
          <p className="font-mono-label text-white/40">CLUB HQ</p>
          <h1 className="font-display text-4xl lg:text-5xl mt-2">MODERATION DESK.</h1>
          <p className="font-mono-label text-white/40 mt-2">{user.email}</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-refresh" onClick={() => window.location.reload()} className="flex items-center gap-2 font-mono-label border border-white/25 px-4 py-3 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
            <RefreshCw size={13} /> REFRESH
          </button>
          <button data-testid="admin-logout" onClick={() => { adminApi.post("/auth/admin/logout").catch(() => {}); setUser(null); }} className="flex items-center gap-2 font-mono-label border border-white/25 px-4 py-3 text-white/60 hover:border-white hover:text-white transition-colors duration-200">
            <LogOut size={13} /> SIGN OUT
          </button>
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 mt-8" data-testid="admin-overview-stats">
          <StatTile label="PENDING REVIEW" value={overview.pending_submissions} testid="stat-pending" />
          <StatTile label="CLUBS" value={overview.clubs} testid="stat-clubs" />
          <StatTile label="VERIFIED" value={overview.verified_clubs} testid="stat-verified" />
          <StatTile label="SUBSCRIBERS" value={overview.subscribers} testid="stat-subscribers" />
          <StatTile label="RSVPS" value={overview.rsvps} testid="stat-rsvps" />
        </div>
      )}

      <div className="flex gap-2 mt-10 mb-8 overflow-x-auto">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            className={`font-mono-label px-5 py-3 border whitespace-nowrap transition-colors duration-200 ${tab === key ? "bg-white text-ink border-white" : "border-white/20 text-white/60 hover:border-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div data-testid="admin-overview-detail">
          <SalesPanel />
          <WaitlistPanel />
          <BlastPanel />
          <div className="border border-white/10 p-8 max-w-xl mt-8">
            <p className="font-mono-label text-white/40 mb-4">PENDING BY TYPE</p>
            {Object.keys(overview.pending_by_type).length === 0 ? (
              <p className="text-white/60 text-sm">Nothing pending. The desk is clear.</p>
            ) : (
              Object.entries(overview.pending_by_type).map(([t, n]) => (
                <div key={t} className="flex justify-between border-b border-white/10 py-3">
                  <span className="font-mono-label text-white/60">{t.toUpperCase()}</span>
                  <span className="font-display text-xl">{n}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {tab === "queue" && <Queue />}
      {tab === "clubs" && <ClubsAdmin />}
      {tab === "orders" && <Orders />}
      {tab === "redo" && <RedoSync />}
      {tab === "stock" && <StockPanel />}
      {tab === "audience" && <Audience />}
      {tab === "outreach" && <Outreach />}
      {tab === "inbox" && <Inbox />}
    </div>
  );
}
